import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AddOrderItemDialog from "@/components/AddOrderItemDialog";
import MarkOrderedDialog from "@/components/MarkOrderedDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardText, Plus, PencilSimple, Trash, CheckCircle, Scroll, Wrench, Package, Truck, CalendarBlank } from "@phosphor-icons/react";

const TYPE_META = {
  film: { label: "Film", Icon: Scroll, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  tools: { label: "Tools", Icon: Wrench, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  other: { label: "Other", Icon: Package, cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function describe(it) {
  if (it.order_type === "other") return it.item_name || "—";
  return [it.manufacturer_name, it.series_name, it.product_name].filter(Boolean).join(" · ") || "—";
}
function qtyLabel(it) {
  if (it.unit === "meters") return `${it.quantity}m`;
  if (it.unit === "units") return `${it.quantity} units`;
  return `${it.quantity} rolls${it.length_per_roll_m ? ` × ${it.length_per_roll_m}m` : ""}`;
}
function fmtDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return d; }
}

function TypeBadge({ type }) {
  const m = TYPE_META[type] || TYPE_META.other;
  const Icon = m.Icon;
  return (
    <span className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 shrink-0 ${m.cls}`}>
      <Icon size={10} /> {m.label}
    </span>
  );
}

export default function ToOrder() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [markItems, setMarkItems] = useState([]);
  const [markOpen, setMarkOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = () => {
    setLoading(true);
    api.get("/order-items")
      .then(({ data }) => setItems(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const pending = items.filter((i) => i.status === "pending");
  const ordered = items.filter((i) => i.status === "ordered");
  const orderedBlocks = useMemo(() => {
    const groups = {};
    for (const it of ordered) {
      const d = (it.ordered_at || "").slice(0, 10) || "—";
      (groups[d] = groups[d] || []).push(it);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [ordered]);

  const onDelete = async (it) => {
    try { await api.delete(`/order-items/${it.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const openAdd = () => { setEditItem(null); setDialogOpen(true); };
  const openEdit = (it) => { setEditItem(it); setDialogOpen(true); };
  const openMark = (it) => { setMarkItems([it]); setMarkOpen(true); };
  const openMarkBulk = () => {
    const chosen = pending.filter((i) => selected.has(i.id));
    if (chosen.length === 0) return;
    setMarkItems(chosen);
    setMarkOpen(true);
  };
  const onMarkSuccess = () => { setSelected(new Set()); load(); };
  const toggleSelect = (id, on) => {
    setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  };
  const allPendingSelected = pending.length > 0 && pending.every((i) => selected.has(i.id));

  return (
    <div className="p-8 max-w-[1200px]" data-testid="to-order-page">
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Overview</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <ClipboardText size={32} /> Ordering
          </h1>
          <p className="text-sm text-slate-600 mt-2">Build a list of what needs ordering. Mark items ordered to file them into dated blocks — film &amp; tools can flow straight into Purchase Orders.</p>
        </div>
        <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="add-order-item-btn">
          <Plus size={16} /> Add item
        </Button>
      </header>

      {/* Pending */}
      <section className="mb-10">
        <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
          <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-3">
            {pending.length > 0 && (
              <Checkbox
                checked={allPendingSelected}
                onCheckedChange={(v) => setSelected(v ? new Set(pending.map((i) => i.id)) : new Set())}
                data-testid="order-select-all"
              />
            )}
            To order <span className="text-slate-400 font-mono text-sm">({pending.length})</span>
          </h2>
          {selected.size > 0 && (
            <Button size="sm" onClick={openMarkBulk} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 rounded-md" data-testid="mark-ordered-bulk-btn">
              <CheckCircle size={15} /> Mark {selected.size} ordered
            </Button>
          )}
        </div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white p-12 text-center rounded-md">
            <ClipboardText size={30} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">Nothing to order right now. Add an item to get started.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((it) => (
              <div key={it.id} className={`flex items-center gap-4 px-4 py-3 bg-white border rounded-md ${selected.has(it.id) ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`} data-testid={`order-item-${it.id}`}>
                <Checkbox
                  checked={selected.has(it.id)}
                  onCheckedChange={(v) => toggleSelect(it.id, v)}
                  data-testid={`order-select-${it.id}`}
                />
                <TypeBadge type={it.order_type} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                    <span className="truncate">{describe(it)}</span>
                    {it.base_sku && <span className="text-[10px] font-mono text-slate-500 bg-slate-100 border border-slate-200 rounded-sm px-1.5 py-0.5 shrink-0" data-testid={`order-sku-${it.id}`}>{it.base_sku}</span>}
                  </div>
                  <div className="text-xs text-slate-500">{qtyLabel(it)}{it.note ? ` · ${it.note}` : ""}</div>
                  {it.purchase_type === "order" && (
                    <div className="text-[11px] text-violet-700 mt-0.5 flex items-center gap-1" data-testid={`order-purpose-${it.id}`}>
                      <ClipboardText size={11} weight="fill" /> For order · {it.order_reference || "—"}
                    </div>
                  )}
                  {it.created_at && (
                    <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1" data-testid={`order-added-${it.id}`}>
                      <CalendarBlank size={11} /> Added {fmtDate(it.created_at.slice(0, 10))}
                    </div>
                  )}
                </div>
                <Button size="sm" onClick={() => openMark(it)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 rounded-md" data-testid={`mark-ordered-${it.id}`}>
                  <CheckCircle size={15} /> Mark ordered
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-order-${it.id}`}><PencilSimple size={15} /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-red-600" data-testid={`delete-order-${it.id}`}><Trash size={15} /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove this item?</AlertDialogTitle>
                      <AlertDialogDescription>"{describe(it)}" will be removed from the order list.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDelete(it)} className="bg-red-600 hover:bg-red-700 rounded-md">Remove</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Ordered, grouped into date blocks */}
      {ordered.length > 0 && (
        <section data-testid="ordered-section">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">
            Ordered <span className="text-slate-400 font-mono text-sm">({ordered.length})</span>
          </h2>
          <div className="space-y-5">
            {orderedBlocks.map(([date, its]) => (
              <div key={date} className="border border-slate-200 bg-white rounded-md overflow-hidden" data-testid={`ordered-block-${date}`}>
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <CalendarBlank size={16} className="text-slate-500" />
                  <span className="font-heading font-semibold text-sm text-slate-800">{fmtDate(date)}</span>
                  <span className="text-xs text-slate-400 font-mono">· {its.length} item{its.length === 1 ? "" : "s"}</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {its.map((it) => (
                    <li key={it.id} className="flex items-center gap-4 px-4 py-3">
                      <TypeBadge type={it.order_type} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                    <span className="truncate">{describe(it)}</span>
                    {it.base_sku && <span className="text-[10px] font-mono text-slate-500 bg-slate-100 border border-slate-200 rounded-sm px-1.5 py-0.5 shrink-0" data-testid={`order-sku-${it.id}`}>{it.base_sku}</span>}
                  </div>
                        <div className="text-xs text-slate-500">{qtyLabel(it)}{it.note ? ` · ${it.note}` : ""}</div>
                        {it.purchase_type === "order" && (
                          <div className="text-[11px] text-violet-700 mt-0.5 flex items-center gap-1" data-testid={`order-purpose-${it.id}`}>
                            <ClipboardText size={11} weight="fill" /> For order · {it.order_reference || "—"}
                          </div>
                        )}
                      </div>
                      {it.po_id ? (
                        <Link to="/incoming-stock" className="text-[10px] font-mono tracking-[0.12em] uppercase px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm inline-flex items-center gap-1 hover:bg-blue-100" data-testid={`po-badge-${it.id}`}>
                          <Truck size={12} /> In Purchase Orders
                        </Link>
                      ) : (
                        <span className="text-[10px] font-mono tracking-[0.12em] uppercase px-2 py-1 bg-slate-100 text-slate-500 border border-slate-200 rounded-sm">Ordered</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      <AddOrderItemDialog open={dialogOpen} onOpenChange={setDialogOpen} editItem={editItem} onSuccess={load} />
      <MarkOrderedDialog items={markItems} open={markOpen} onOpenChange={setMarkOpen} onSuccess={onMarkSuccess} />
    </div>
  );
}
